import { Link } from "wouter-preact"
import { ButtonLink } from "@/components/ui/button"
import { classes } from "@/utils"
import type { Integration } from "@/api/integration"

/**
 * Kept in sync with `xero_client::REAUTH_MESSAGE`. The server sends this exact string as the apply /
 * publish error whenever Xero rejects our refresh token, so the UI can recognize it and offer the
 * one action that resolves it instead of rendering it as another generic failure.
 */
export const XERO_REAUTH_MESSAGE =
  "Your Xero connection has expired and needs to be reconnected. Go to Settings → Connections, reconnect Xero, then try again."

/**
 * Shown before an apply when the Xero connection was authorized without `accounting.attachments`
 * (every connection made before MarginWiz started asking for it). Xero only adds a scope when the
 * user consents again, so reconnecting is the one fix.
 */
export const XERO_ATTACHMENTS_RECONNECT_MESSAGE =
  "Xero hasn't given MarginWiz permission to attach files, so these credit notes would go up without each deduction's backup. Reconnect Xero first to include it — applying now books the credit notes without backup."

/** Where the user re-authorizes Xero. */
export const XERO_CONNECT_PATH = "/settings/connect/xero"

/**
 * Whether an error means the Xero connection itself is dead rather than the operation failing.
 *
 * Matches the friendly message and the raw OAuth text, because a `invalid_grant` can still reach the
 * UI from paths that surface the provider error verbatim (job errors, older stored `publish_error`
 * rows written before the server started translating them).
 */
export function isXeroReauthError(message: string | null | undefined): boolean {
  if (!message) {
    return false
  }
  return message.includes(XERO_REAUTH_MESSAGE) || message.includes("invalid_grant")
}

/** Whether this integration is flagged as needing the user to re-authorize Xero. */
export function xeroNeedsReauth(integration: Pick<Integration, "name" | "data"> | undefined): boolean {
  if (!integration || integration.name !== "xero") {
    return false
  }
  const data = integration.data as { reauth_required_at?: string | null } | undefined
  return Boolean(data?.reauth_required_at)
}

interface XeroReconnectNoticeProps {
  /** Extra context to show above the action, e.g. which step failed. Optional. */
  message?: string
  /** `warning` when reconnecting unlocks something optional rather than something the action needs. */
  tone?: "error" | "warning"
  className?: string
}

/**
 * The banner shown when Xero needs re-authorizing. Xero rotates its refresh token on every use and
 * expires it after 60 days, so a connection that has been idle or interrupted can only be revived by
 * the user re-consenting — retrying accomplishes nothing, so lead with the button.
 */
export function XeroReconnectNotice({ message, tone = "error", className }: XeroReconnectNoticeProps) {
  return (
    <div
      class={classes(
        "rounded-md border px-4 py-3 text-sm space-y-3",
        tone === "warning" ? "border-amber-200 bg-amber-50 text-amber-800" : "border-red-200 bg-red-50 text-red-700",
        className
      )}>
      <div>{message ?? XERO_REAUTH_MESSAGE}</div>
      {/* `ButtonLink`, not `<Link><Button/></Link>`: wouter clones its single child rather than
          wrapping it, so a `Button` child would come out as `<button href=…>` — invalid markup that
          assistive tech doesn't announce as a link. Cloning onto an `<a>` gives a real link that
          wouter still intercepts for client-side navigation. */}
      <Link href={XERO_CONNECT_PATH}>
        <ButtonLink href={XERO_CONNECT_PATH} size="sm" variant="outline">
          Reconnect Xero
        </ButtonLink>
      </Link>
    </div>
  )
}

if (import.meta.vitest) {
  const { describe, it, expect } = import.meta.vitest

  describe("isXeroReauthError", () => {
    it("matches the server's reconnect message", () => {
      expect(isXeroReauthError(XERO_REAUTH_MESSAGE)).toBe(true)
    })

    it("matches the raw OAuth error Compostic hit on 2026-08-11", () => {
      expect(
        isXeroReauthError(
          'Xero create_credit_note API call failed: HTTP 400 {"error":"invalid_grant","error_description":"Refresh token has been consumed"}'
        )
      ).toBe(true)
    })

    it("leaves ordinary apply failures alone", () => {
      expect(isXeroReauthError("Credit memo created, but the payment failed: HTTP 400 Account code 5037 is invalid")).toBe(false)
      expect(isXeroReauthError(null)).toBe(false)
      expect(isXeroReauthError("")).toBe(false)
    })
  })

  describe("xeroNeedsReauth", () => {
    it("is true only when the Xero integration carries the flag", () => {
      expect(xeroNeedsReauth({ name: "xero", data: { reauth_required_at: "2026-08-11T19:35:57Z" } })).toBe(true)
      expect(xeroNeedsReauth({ name: "xero", data: { reauth_required_at: null } })).toBe(false)
      expect(xeroNeedsReauth({ name: "xero", data: {} })).toBe(false)
      expect(xeroNeedsReauth(undefined)).toBe(false)
    })

    it("ignores other providers that happen to carry the key", () => {
      expect(xeroNeedsReauth({ name: "quickbooks", data: { reauth_required_at: "2026-08-11T19:35:57Z" } })).toBe(false)
    })
  })
}
